import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FiCheckCircle, FiAlertTriangle, FiXCircle, FiFileText,
  FiUser, FiClock, FiEdit3, FiDownload,
} from 'react-icons/fi';
import { verificationAPI } from '../../services/api';
import { getLocalized } from '../../i18n/getLocalized';
import { formatDateMedium, formatDateTime, setDateRegion } from '../../utils/dateFormat';
import { localizeSeeded } from '../FlowManagement/flowUtils';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { SUPPORTED_LANGUAGES } from '../../i18n/supportedLanguages';
import { applyLanguage, hasExplicitLanguageChoice, markExplicitLanguageChoice } from '../../i18n';
import './VerifyContract.scss';

/**
 * Public QR verification page — opened by scanning the QR stamped on an
 * approved (locked) contract document. Deliberately generic: shows only the
 * whitelisted summary the backend returns; no login, no navigation into the
 * app, no comments or internal workflow details.
 */
function VerifyContract() {
  const { code } = useParams();
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.split('-')[0] || 'en';

  const [state, setState] = useState({ loading: true, data: null, status: null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, data: null, status: null });
    verificationAPI.get(code)
      .then(({ data }) => {
        if (!cancelled) setState({ loading: false, data, status: data.status });
      })
      .catch((err) => {
        if (!cancelled) {
          setState({
            loading: false,
            data: null,
            status: err.response?.data?.status || 'not_found',
          });
        }
      });
    return () => { cancelled = true; };
  }, [code]);

  // The QR is scanned on a device that has never opened the app, so there is
  // no stored preference and detection falls back to English. Start in the
  // tenant's own default language instead — unless this visitor has already
  // picked one here, which always wins.
  const tenantDefaultLanguage = state.data?.tenant?.defaultLanguage;
  useEffect(() => {
    if (!tenantDefaultLanguage) return;
    if (hasExplicitLanguageChoice()) return;
    if (tenantDefaultLanguage === lang) return;
    applyLanguage(tenantDefaultLanguage);
  }, [tenantDefaultLanguage, lang]);

  // This page renders outside the app Layout, which is what normally sets the
  // date region — without this, dates would follow the scanning phone's locale
  // while the rest of the page is in the tenant's language.
  useEffect(() => { setDateRegion(lang); }, [lang]);

  const switchLang = (next) => {
    markExplicitLanguageChoice();
    applyLanguage(next);
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      const res = await verificationAPI.downloadDocument(code);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const number = state.data?.contract?.contractNumber || 'contract';
      const version = state.data?.document?.versionNumber || 1;
      a.download = `${number}_approved_v${version}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setDownloadError(true);
    } finally {
      setDownloading(false);
    }
  };

  const { loading, data, status } = state;
  const accent = data?.tenant?.accentColor || '#6366f1';

  const processStatusLabel = (s) =>
    t(`verifyPage.statuses.${s}`, { defaultValue: (s || '').replace(/_/g, ' ') });
  const signatureStateLabel = (s) =>
    t(`verifyPage.signatureStates.${s}`, { defaultValue: (s || '').replace(/_/g, ' ') });

  return (
    <div className="vfy" style={{ '--vfy-accent': accent }}>
      <div className="vfy__container">
        {/* Shows the language the page is currently in, and opens a list to
            switch — not a toggle labelled with the *other* language. */}
        <div className="vfy__lang">
          <LanguageSwitcher
            languages={SUPPORTED_LANGUAGES}
            current={lang}
            onChange={switchLang}
          />
        </div>

        {loading && (
          <div className="vfy__card vfy__card--center">
            <div className="vfy__spinner" />
            <p>{t('verifyPage.loading')}</p>
          </div>
        )}

        {!loading && status === 'not_found' && (
          <div className="vfy__card vfy__card--center">
            <div className="vfy__badge vfy__badge--error"><FiXCircle size={26} /></div>
            <h1>{t('verifyPage.invalidTitle')}</h1>
            <p className="vfy__muted">{t('verifyPage.invalidDesc')}</p>
          </div>
        )}

        {!loading && status === 'superseded' && (
          <div className="vfy__card vfy__card--center">
            <div className="vfy__badge vfy__badge--warn"><FiAlertTriangle size={26} /></div>
            <h1>{t('verifyPage.supersededTitle')}</h1>
            <p className="vfy__muted">{t('verifyPage.supersededDesc')}</p>
          </div>
        )}

        {!loading && status === 'valid' && data && (
          <>
            {/* ── Header: tenant + verified badge ── */}
            <div className="vfy__card">
              <div className="vfy__tenant">
                {data.tenant?.logoUrl && (
                  <img src={data.tenant.logoUrl} alt="" className="vfy__logo" />
                )}
                <span className="vfy__tenant-name">{getLocalized(data.tenant?.name, lang)}</span>
              </div>

              <div className="vfy__verified">
                <div className="vfy__badge vfy__badge--ok"><FiCheckCircle size={26} /></div>
                <div>
                  <h1>{t('verifyPage.verifiedBadge')}</h1>
                  <p className="vfy__muted">{t('verifyPage.verifiedDesc')}</p>
                </div>
              </div>

              {/* ── Contract summary ── */}
              <div className="vfy__grid">
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.contractNo')}</span>
                  <span className="vfy__value">{data.contract.contractNumber || '—'}</span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.contractName')}</span>
                  <span className="vfy__value">{data.contract.contractName || '—'}</span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.contractType')}</span>
                  <span className="vfy__value">{getLocalized(data.contract.contractType, lang) || '—'}</span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.counterparty')}</span>
                  <span className="vfy__value">{data.contract.counterpartyName || '—'}</span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.contractVersion')}</span>
                  <span className="vfy__value">
                    V{data.contract.majorVersion ?? 0}
                    {' — '}
                    {t(`verifyPage.versionTypes.${data.contract.versionType || 'original'}`, {
                      defaultValue: data.contract.versionType || 'original',
                    })}
                  </span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.approvedVersion')}</span>
                  <span className="vfy__value">
                    <FiFileText size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                    {data.document?.title ? `${data.document.title} — ` : ''}v{data.document?.versionNumber}
                  </span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.lockedAt')}</span>
                  <span className="vfy__value">
                    {data.document?.lockedAt ? formatDateTime(data.document.lockedAt) : '—'}
                  </span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.approvalCompletedAt')}</span>
                  <span className="vfy__value">
                    {data.approvalCompletedAt ? formatDateTime(data.approvalCompletedAt) : '—'}
                  </span>
                </div>
                <div className="vfy__field">
                  <span className="vfy__label">{t('verifyPage.processStatus')}</span>
                  <span className="vfy__value vfy__value--status">
                    {processStatusLabel(data.contract.status)}
                  </span>
                </div>
              </div>

              <div className="vfy__download-row">
                <button
                  type="button"
                  className="vfy__download"
                  onClick={handleDownload}
                  disabled={downloading}
                >
                  <FiDownload size={14} />
                  {downloading
                    ? t('verifyPage.downloading')
                    : t('verifyPage.downloadDocument')}
                </button>
                {downloadError && (
                  <span className="vfy__download-error">{t('verifyPage.downloadFailed')}</span>
                )}
              </div>
            </div>

            {/* ── Approval history (simplified: who, action, result) ── */}
            {data.approvals?.length > 0 && (
              <div className="vfy__card">
                <h2 className="vfy__section-title">{t('verifyPage.approvalHistory')}</h2>
                <ul className="vfy__approvals">
                  {data.approvals.map((a, i) => (
                    <li key={i} className="vfy__approval">
                      <span className="vfy__approval-avatar"><FiUser size={14} /></span>
                      <span className="vfy__approval-main">
                        <span className="vfy__approval-name">{a.name}</span>
                        {/* Seeded step names are stored as "TR / EN" — show
                            only the half for the active language. */}
                        {a.stepName && (
                          <span className="vfy__approval-step">{localizeSeeded(a.stepName, lang)}</span>
                        )}
                      </span>
                      <span className={`vfy__pill vfy__pill--${a.action}`}>
                        {a.action === 'reviewed'
                          ? t('verifyPage.actions.reviewed')
                          : t('verifyPage.actions.approved')}
                      </span>
                      {a.decidedAt && (
                        <span className="vfy__approval-date">
                          <FiClock size={11} /> {formatDateMedium(a.decidedAt)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* ── Digital signature status ──
                Only for genuinely digital flows: in-house e-signature or
                DocuSign. A manually uploaded signed copy has no digital
                signature, so the section stays hidden for it. */}
            {['normal', 'docusign'].includes(data.contract.signatureMethod)
              && data.contract.signatureState
              && data.contract.signatureState !== 'not_sent' && (
              <div className="vfy__card">
                <h2 className="vfy__section-title">{t('verifyPage.digitalSignature')}</h2>
                <div className="vfy__grid">
                  <div className="vfy__field">
                    <span className="vfy__label">{t('verifyPage.signatureStatusLabel')}</span>
                    <span className="vfy__value vfy__value--status">
                      <FiEdit3 size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                      {signatureStateLabel(data.contract.signatureState)}
                    </span>
                  </div>
                  <div className="vfy__field">
                    <span className="vfy__label">{t('verifyPage.signatureMethodLabel')}</span>
                    <span className="vfy__value">
                      {data.contract.signatureMethod === 'docusign'
                        ? 'DocuSign'
                        : t('verifyPage.signatureMethods.normal')}
                    </span>
                  </div>
                  {data.contract.signingDate && (
                    <div className="vfy__field">
                      <span className="vfy__label">{t('verifyPage.signingDate')}</span>
                      <span className="vfy__value">{formatDateMedium(data.contract.signingDate)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="vfy__footer">
              {t('verifyPage.footerNote', { tenant: getLocalized(data.tenant?.name, lang) || '' })}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

export default VerifyContract;
